import type { CSSProperties, KeyboardEvent, ReactElement, ReactNode } from 'react';
import React, { useRef, useState } from 'react';
import {
  type UseRoleProps,
  arrow,
  autoUpdate,
  FloatingPortal,
  hide,
  offset,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useFocus,
  useHover,
  useInteractions,
  useRole,
  useTransitionStatus,
} from '@floating-ui/react';
import styled from 'styled-components';

import type Color from '@rover/kibble/styles/Color';
import useTheme from '@rover/kibble/themes/useTheme';
import { onSpaceOrEnterKeyPress } from '@rover/react-lib/src/utils/input';
import { isHoverable } from '@rover/rsdk/src/modules/Network/userAgent';

import { PopoverEvent, PopoverPlacement } from './popover.constants';
import StyledPopover, { TRANSITION_DURATION } from './StyledPopover';
import TargetWrapper from './TargetWrapper';

export const MOUSE_LEAVE_DELAY = 100;
const OFFSET_DISTANCE = 8;

type RenderProp = (arg0: { onClose: () => void }) => ReactNode;

type PreventOverflow = {
  enabled?: boolean;
  boundariesElement?: 'viewport' | 'window' | 'scrollParent';
};

export type Props = {
  id?: string;
  children: ReactNode;
  className?: string;
  content: ReactNode | RenderProp;
  open?: boolean;
  placement?: PopoverPlacement;
  event?: PopoverEvent;
  backgroundColor?: Color | string;
  borderColor?: Color | string;
  display?: 'inline' | 'inline-block' | 'block';
  showArrow?: boolean;
  toggleOnFocusOrBlur?: boolean;
  toggleOnSpaceOrEnter?: boolean;
  onChange?: (arg0: boolean) => void;
  role?: string;
  maxWidth?: string;
  preventOverflow?: PreventOverflow;
  'data-testid'?: string;
  // When true, renders the popover content via a React portal to document.body,
  // so it escapes any ancestor stacking context and its z-index is honored.
  usePortal?: boolean;
};

type PopoverComponent = React.FC<Props> & {
  Event: typeof PopoverEvent;
  Placement: typeof PopoverPlacement;
};

export const ContentWrapper = styled.div`
  background: rgba(0, 0, 0, 0);
  -webkit-font-smoothing: antialiased;
  /* this max-width helps prevent Popovers from being wider than the screen, which can trigger a resize loop */
  max-width: calc(100vw - 60px);
`;

const PopoverInner: React.FC<Props> = (props): ReactElement => {
  const {
    children,
    content,
    placement: requestedPlacement = PopoverPlacement.BOTTOM,
    display = 'inline',
    preventOverflow = { enabled: false },
    event = PopoverEvent.HOVER,
    open: openProp,
    backgroundColor,
    borderColor,
    showArrow = true,
    toggleOnFocusOrBlur = false,
    toggleOnSpaceOrEnter = false,
    onChange,
    role: roleProp,
    maxWidth,
    className,
    id,
    'data-testid': dataTestId,
    usePortal = false,
    ...other
  } = props;

  const isControlled = event === PopoverEvent.NONE;
  const hoverable = typeof window !== 'undefined' && isHoverable();

  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const open = isControlled ? Boolean(openProp) : uncontrolledOpen;

  const handleOpenChange = (next: boolean): void => {
    if (isControlled) return;
    setUncontrolledOpen(next);
    if (onChange) onChange(next);
  };

  const arrowRef = useRef<HTMLDivElement | null>(null);

  const { refs, floatingStyles, context, placement, middlewareData } = useFloating({
    open,
    onOpenChange: handleOpenChange,
    placement: requestedPlacement,
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(OFFSET_DISTANCE),
      ...(preventOverflow?.enabled ? [shift()] : []),
      hide(),
      ...(showArrow ? [arrow({ element: arrowRef })] : []),
    ],
  });

  const hoverEnabled = !isControlled && event === PopoverEvent.HOVER && hoverable;
  const clickEnabled =
    !isControlled &&
    !toggleOnFocusOrBlur &&
    (event === PopoverEvent.CLICK || (event === PopoverEvent.HOVER && !hoverable));

  const hover = useHover(context, {
    enabled: hoverEnabled,
    delay: { close: MOUSE_LEAVE_DELAY },
    move: false,
  });
  const click = useClick(context, {
    enabled: clickEnabled,
    toggle: true,
    keyboardHandlers: toggleOnSpaceOrEnter,
  });
  const dismiss = useDismiss(context, {
    enabled: !isControlled,
    escapeKey: true,
    outsidePress: true,
  });
  const focus = useFocus(context, {
    enabled: !isControlled && toggleOnFocusOrBlur,
    visibleOnly: false,
  });
  const role = useRole(context, {
    enabled: roleProp != null,
    role: roleProp as UseRoleProps['role'],
  });

  const { getReferenceProps, getFloatingProps } = useInteractions([
    hover,
    click,
    dismiss,
    focus,
    role,
  ]);

  const { isMounted, status } = useTransitionStatus(context, {
    duration: TRANSITION_DURATION,
  });

  const theme = useTheme();
  const resolvedBackgroundColor = backgroundColor ?? theme.background.primary;
  const resolvedBorderColor = borderColor ?? theme.colors.border.primary;

  const renderContent = (): ReactNode => {
    if (typeof content === 'function') {
      return content({ onClose: () => handleOpenChange(false) });
    }
    return content;
  };

  const handleClose = (): void => handleOpenChange(false);

  const referenceProps = getReferenceProps();
  const composedReferenceProps: Record<string, unknown> = { ...referenceProps };

  if (toggleOnSpaceOrEnter && !clickEnabled) {
    const existingOnKeyDown = referenceProps.onKeyDown as ((e: KeyboardEvent) => void) | undefined;
    composedReferenceProps.onKeyDown = (e: KeyboardEvent): void => {
      existingOnKeyDown?.(e);
      onSpaceOrEnterKeyPress(e, () => handleOpenChange(!open));
    };
  }

  const arrowX = middlewareData.arrow?.x;
  const arrowY = middlewareData.arrow?.y;
  // Use `escaped` rather than `referenceHidden` to drive visibility.
  // `referenceHidden` reports true in jsdom, which would hide the popover
  // and any role="alert" from accessibility queries.
  const referenceHidden = middlewareData.hide?.escaped ?? false;

  const arrowStyleVars: CSSProperties = {
    ...(arrowX != null ? { ['--arrow-x' as string]: `${arrowX}px` } : {}),
    ...(arrowY != null ? { ['--arrow-y' as string]: `${arrowY}px` } : {}),
  };

  const popoverElement = isMounted ? (
    <StyledPopover
      {...other}
      ref={refs.setFloating}
      style={{ ...(floatingStyles as CSSProperties), ...arrowStyleVars }}
      data-placement={placement}
      data-status={status}
      data-testid={dataTestId}
      backgroundColor={resolvedBackgroundColor}
      borderColor={resolvedBorderColor}
      showArrow={showArrow}
      isOutOfBoundaries={referenceHidden}
      maxWidth={maxWidth}
      className={className}
      {...getFloatingProps({ onMouseLeave: handleClose })}
    >
      <ContentWrapper>{renderContent()}</ContentWrapper>
      {showArrow && (
        <div
          ref={arrowRef}
          aria-hidden="true"
          style={{ position: 'absolute', width: 0, height: 0 }}
        />
      )}
    </StyledPopover>
  ) : null;

  return (
    <>
      <TargetWrapper
        ref={refs.setReference}
        event={event}
        display={display}
        data-testid="styled-wrapper"
        id={id}
        {...composedReferenceProps}
      >
        {children}
      </TargetWrapper>
      {usePortal ? <FloatingPortal>{popoverElement}</FloatingPortal> : popoverElement}
    </>
  );
};

const Popover = PopoverInner as PopoverComponent;
Popover.displayName = 'Popover';
Popover.Event = PopoverEvent;
Popover.Placement = PopoverPlacement;

export default Popover;
